/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { useTranslation } from 'react-i18next';
import { DynamicImage } from '@/components/dynamic-image';
import { Typography } from '@/components/typography';
import type { SomOrderItem } from '@/lib/order/guest-lookup.server';

const THUMBNAIL_WIDTHS = { base: 96, md: 112 };

export interface OrderLineItemProps {
    item: SomOrderItem;
}

/**
 * One order line in the guest Track Order view: thumbnail, product name (+ quantity when
 * more than one), line total, and variation attributes. Mirrors SFRA's `order/orderItems`
 * template. Prices are pre-formatted display strings from the SOM model.
 */
export function OrderLineItem({ item }: OrderLineItemProps) {
    const { t } = useTranslation('trackOrder');
    const image = item.images?.small?.[0];
    const quantity = typeof item.quantity === 'number' ? item.quantity : undefined;

    return (
        <div className="flex gap-4 py-4">
            <div className="w-20 shrink-0 sm:w-24">
                {image?.url ? (
                    <DynamicImage
                        src={image.url}
                        alt={image.alt ?? item.productName ?? ''}
                        widths={THUMBNAIL_WIDTHS}
                        className="aspect-square w-full rounded-ui object-cover"
                    />
                ) : (
                    <div className="aspect-square w-full rounded-ui bg-muted" aria-hidden />
                )}
            </div>

            <div className="min-w-0 flex-1">
                <Typography variant="p" className="font-medium">
                    {item.productName}
                    {quantity && quantity > 1 ? (
                        <span className="text-muted-foreground"> {t('item.quantity', { count: quantity })}</span>
                    ) : null}
                </Typography>

                {item.totalPrice ? (
                    <Typography variant="p" className="mt-1 text-sm text-muted-foreground">
                        {item.totalPrice}
                    </Typography>
                ) : null}

                {item.variationAttributes && item.variationAttributes.length > 0 ? (
                    <ul className="mt-1 space-y-0.5">
                        {item.variationAttributes.map((attribute) => (
                            <li
                                key={`${attribute.displayName}-${attribute.displayValue}`}
                                className="text-sm text-muted-foreground">
                                {attribute.displayName}: {attribute.displayValue}
                            </li>
                        ))}
                    </ul>
                ) : null}
            </div>
        </div>
    );
}
